import { HStack, RadioGroup, Separator, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { getParetoAnalysis } from "../../api/workspace";
import {
  BiddingStrategy,
  OptimizationTarget,
} from "../../types/optimization-rule";
import { Button } from "../button";
import { CloseButton } from "../close-button";
import { Field } from "../field";
import { Info } from "../info";
import { NumberInputField, NumberInputRoot } from "../number-input";
import { StepContent } from "./step-content";
import { OptimizationFormData } from "./types";

interface BiddingStepProps {
  workspaceSlug: string;
}

function RadioFieldWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Stack gap="2" ml={"calc(20px + 0.625rem)" as any}>
      <Separator />
      {children}
    </Stack>
  );
}

export function BiddingStep({ workspaceSlug }: BiddingStepProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext<OptimizationFormData>();
  const [paretoValue, setParetoValue] = React.useState<number | null>(null);
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const target = useWatch({ control, name: "target" });
  const biddingStrategy = useWatch({ control, name: "biddingStrategy" });
  const strategyOptions = React.useMemo(() => {
    switch (target) {
      case OptimizationTarget.CP:
        return [
          {
            label: "Value-Based",
            description: `Will maximize the impact of ROAS AI. This is the recommended method for lowering CPAs.`,
            value: BiddingStrategy.Value,
          },
          {
            label: "Lowest CPA",
            description: `Will help Meta bid on higher intent users.`,
            value: BiddingStrategy.LowestCPA,
          },
        ];
      default:
        return [
          {
            label: "Value-Based",
            description: `Will maximize the impact of ROAS AI. May increase CPAs more than Lowest CPA but will also increase ROAS more.`,
            value: BiddingStrategy.Value,
          },
          {
            label: "Lowest CPA",
            description: `Maintains lowest possible CPA for high value conversions.`,
            value: BiddingStrategy.LowestCPA,
          },
        ];
    }
  }, [target]);

  React.useEffect(() => {
    const fetchPareto = async () => {
      const analysis = await getParetoAnalysis(
        workspaceSlug,
        target === OptimizationTarget.CP ? "visitor" : "customer",
      );
      setParetoValue(analysis.pareto);
    };
    fetchPareto();
  }, [target, workspaceSlug]);

  const recommendedPercentage = paretoValue
    ? Math.round(paretoValue * 100)
    : null;

  const avgOrderValueField = (
    <RadioFieldWrapper>
      <Field
        invalid={!!errors.avgOrderValue}
        errorText={errors.avgOrderValue?.message}
        label="Average Order Value"
      >
        <Controller
          name="avgOrderValue"
          control={control}
          render={({ field }) => (
            <HStack gap="2">
              <NumberInputRoot
                size="xs"
                min={0}
                value={field.value && String(field.value)}
                onValueChange={({ value }) =>
                  field.onChange(value === "" ? null : Number(value))
                }
                onBlur={field.onBlur}
              >
                <NumberInputField />
              </NumberInputRoot>
            </HStack>
          )}
        />
      </Field>
    </RadioFieldWrapper>
  );

  const createPercentileCutoffField = (
    fieldName: "customValueCutoff" | "customCpaCutoff",
    allowEmpty = false,
  ) => (
    <RadioFieldWrapper>
      <Field
        invalid={!!errors[fieldName]}
        errorText={errors[fieldName]?.message}
        label="Percentile Cutoff"
      >
        <Controller
          name={fieldName}
          control={control}
          render={({ field }) => (
            <HStack gap="2">
              <Text>Top</Text>
              <NumberInputRoot
                color="fg.muted"
                max={100}
                min={allowEmpty ? 0 : 1}
                size="xs"
                step={1}
                width="20"
                value={field.value && Math.round(field.value * 100).toString()}
                onBlur={field.onBlur}
                onValueChange={(e) => {
                  field.onChange(
                    e.value === "" ? null : Number(e.value) / 100.0,
                  );
                }}
              >
                <NumberInputField />
              </NumberInputRoot>
              <Text>%</Text>
            </HStack>
          )}
        />
        {recommendedPercentage && (
          <Info>
            We recommend top {recommendedPercentage}% based on your customer
            data, as this percentage of users is responsible for 80% of your
            order revenue.
          </Info>
        )}
      </Field>
    </RadioFieldWrapper>
  );

  return (
    <StepContent title="Bidding Strategy">
      <Field
        invalid={!!errors.biddingStrategy}
        errorText={errors.biddingStrategy?.message}
      >
        <Controller
          name="biddingStrategy"
          control={control}
          render={({ field }) => (
            <RadioGroup.Root
              colorPalette="blue"
              name={field.name}
              onBlur={field.onBlur}
              onValueChange={(e) => field.onChange(e.value)}
              value={field.value}
            >
              <Stack gap="2">
                {strategyOptions.map((option) => (
                  <Stack
                    key={option.value}
                    borderRadius="md"
                    borderWidth="1"
                    p="2"
                  >
                    <RadioGroup.Item alignItems="start" value={option.value}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText asChild>
                        <Stack flex="1" gap="0">
                          {option.label}
                          <Text color="fg.muted" fontSize="xs">
                            {option.description}
                          </Text>
                        </Stack>
                      </RadioGroup.ItemText>
                    </RadioGroup.Item>
                    {target === OptimizationTarget.CP &&
                      option.value === BiddingStrategy.Value &&
                      field.value === BiddingStrategy.Value && (
                        <>
                          {avgOrderValueField}
                          {createPercentileCutoffField(
                            "customValueCutoff",
                            true,
                          )}
                        </>
                      )}
                    {option.value === BiddingStrategy.LowestCPA &&
                      field.value === BiddingStrategy.LowestCPA &&
                      createPercentileCutoffField("customCpaCutoff")}
                  </Stack>
                ))}
              </Stack>
            </RadioGroup.Root>
          )}
        />
      </Field>
      {target !== OptimizationTarget.CP &&
        biddingStrategy === BiddingStrategy.Value && (
          <>
            <Button
              colorPalette="gray"
              variant="plain"
              size="xs"
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? "Hide" : "Show"} Advanced Settings
            </Button>
            {showAdvanced && (
              <Stack mt="2" gap="4" borderWidth="1" borderRadius="md" p="4">
                <Field
                  invalid={!!errors.maxValue}
                  errorText={errors.maxValue?.message}
                  label="Max Value"
                  helperText="Sets an upper limit on the per-event values sent to Meta."
                  orientation="vertical"
                >
                  <Controller
                    name="maxValue"
                    control={control}
                    render={({ field }) => (
                      <HStack gap="2">
                        <NumberInputRoot
                          size="xs"
                          min={0}
                          value={field.value && String(field.value)}
                          onValueChange={({ value }) =>
                            field.onChange(value === "" ? null : Number(value))
                          }
                          onBlur={field.onBlur}
                        >
                          <NumberInputField />
                        </NumberInputRoot>
                        {field.value != null && (
                          <CloseButton
                            size="xs"
                            aria-label="Clear"
                            colorPalette="gray"
                            onClick={() => field.onChange(null)}
                          />
                        )}
                      </HStack>
                    )}
                  />
                </Field>
                <Field
                  invalid={!!errors.maxValuePercentile}
                  errorText={errors.maxValuePercentile?.message}
                  label="Max Value Percentile"
                  helperText="Sets an upper limit on the per-event values sent to Meta based on the given percentile of order values. If both a max and a max percentile are provided, the lower of the two numbers will be used."
                  orientation="vertical"
                >
                  <Controller
                    name="maxValuePercentile"
                    control={control}
                    render={({ field }) => (
                      <HStack gap="2">
                        <NumberInputRoot
                          size="xs"
                          min={0}
                          max={100}
                          value={
                            field.value && String(Math.round(field.value * 100))
                          }
                          onValueChange={({ value }) =>
                            field.onChange(
                              value === "" ? null : Number(value) / 100,
                            )
                          }
                          onBlur={field.onBlur}
                        >
                          <NumberInputField />
                        </NumberInputRoot>
                        <Text>%</Text>
                        {field.value != null && (
                          <CloseButton
                            size="xs"
                            aria-label="Clear"
                            colorPalette="gray"
                            onClick={() => field.onChange(null)}
                          />
                        )}
                      </HStack>
                    )}
                  />
                </Field>
              </Stack>
            )}
          </>
        )}
    </StepContent>
  );
}
